import { useState } from 'react'
import type { FormEvent } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export const LoginComponent = () => {
  const { signIn, signInWithGoogle } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [googleSubmitting, setGoogleSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitting(true)

    const { error } = await signIn(email.trim(), password)

    if (error) {
      toast.error(error.message)
    }

    setSubmitting(false)
  }

  const handleGoogleSignIn = async () => {
    setGoogleSubmitting(true)

    const { error } = await signInWithGoogle()

    if (error) {
      toast.error(error.message)
      setGoogleSubmitting(false)
    }
  }

  return (
    <div className='flex min-h-screen items-center justify-center p-6'>
      <Card className='w-full max-w-sm'>
        <CardHeader>
          <CardTitle>Booky</CardTitle>
          <CardDescription>Sign in with an existing account.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type='button'
            variant='outline'
            className='mb-4 w-full gap-2'
            onClick={handleGoogleSignIn}
            disabled={googleSubmitting || submitting}
          >
            <svg aria-hidden='true' viewBox='0 0 24 24' className='h-4 w-4'>
              <path
                fill='#EA4335'
                d='M12 10.2v3.9h5.5c-.2 1.3-1.5 3.9-5.5 3.9-3.3 0-6-2.8-6-6.2s2.7-6.2 6-6.2c1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 2.9 14.7 2 12 2 6.5 2 2 6.5 2 12s4.5 10 10 10c5.8 0 9.6-4.1 9.6-9.8 0-.7-.1-1.2-.2-1.8H12z'
              />
              <path
                fill='#34A853'
                d='M3.2 7.3l3.2 2.3C7.2 8 9.4 6.4 12 6.4c1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 2.9 14.7 2 12 2 8.1 2 4.8 4.2 3.2 7.3z'
              />
              <path
                fill='#FBBC05'
                d='M12 22c2.6 0 4.8-.9 6.4-2.5l-3-2.4c-.8.6-1.9 1-3.4 1-2.9 0-5.4-2-6.3-4.6l-3.2 2.5C4.1 19.4 7.7 22 12 22z'
              />
              <path
                fill='#4285F4'
                d='M22 12c0-.7-.1-1.2-.2-1.8H12v3.9h5.5c-.3 1.8-1.4 3.2-2.9 4.1l3 2.4C20.4 18.8 22 15.8 22 12z'
              />
            </svg>
            {googleSubmitting ? 'Redirecting...' : 'Continue with Google'}
          </Button>
          <form onSubmit={handleSubmit} className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='email'>Email</Label>
              <Input
                id='email'
                type='email'
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete='email'
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='password'>Password</Label>
              <Input
                id='password'
                type='password'
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete='current-password'
                required
              />
            </div>
            <Button type='submit' className='w-full' disabled={submitting}>
              {submitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
